import React, { useEffect, useState } from 'react';
import '../styles/batches.css';

const EMPTY_BATCH = {
  product_id: '',
  batch_number: '',
  purchase_date: new Date().toLocaleDateString('en-CA'),
  expiry_date: '',
  quantity_received: '',
  purchase_price: '',
  selling_price: '',
  hsn_code: '',
  gst_rate: '0',
};

const BatchForm = ({
  products = [],
  onSubmit,
  submitLabel = 'Save Batch',
  loading = false,
}) => {
  const [form, setForm] = useState(EMPTY_BATCH);
  const [errors, setErrors] = useState({});

  const selectedProduct = products.find(
    (product) => String(product.id) === String(form.product_id)
  );

  useEffect(() => {
    if (!selectedProduct) return;

    setForm((previous) => ({
      ...previous,
      hsn_code: previous.hsn_code || selectedProduct.hsn_code || '',
      gst_rate:
        previous.gst_rate === '0' && selectedProduct.gst_rate != null
          ? String(selectedProduct.gst_rate)
          : previous.gst_rate,
    }));
  }, [selectedProduct]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
      ...(name === 'product_id'
        ? {
            hsn_code: '',
            gst_rate: '0',
          }
        : {}),
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: '',
      ...(name === 'product_id'
        ? { hsn_code: '', gst_rate: '' }
        : {}),
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.product_id) {
      nextErrors.product_id = 'Please select a product.';
    }

    if (!form.batch_number.trim()) {
      nextErrors.batch_number = 'Batch number is required.';
    } else if (form.batch_number.trim().length > 100) {
      nextErrors.batch_number = 'Maximum 100 characters.';
    }

    if (!form.purchase_date) {
      nextErrors.purchase_date = 'Purchase date is required.';
    }

    if (
      form.expiry_date &&
      form.expiry_date < form.purchase_date
    ) {
      nextErrors.expiry_date =
        'Expiry date cannot be before the purchase date.';
    }

    const quantity = Number(form.quantity_received);

    if (
      form.quantity_received.trim() === '' ||
      !Number.isFinite(quantity) ||
      quantity <= 0
    ) {
      nextErrors.quantity_received =
        'Quantity received must be greater than zero.';
    }

    for (const field of ['purchase_price', 'selling_price']) {
      const value = Number(form[field]);

      if (
        form[field].trim() === '' ||
        !Number.isFinite(value) ||
        value < 0
      ) {
        nextErrors[field] = 'Enter a valid non-negative price.';
      }
    }

    const gst = Number(form.gst_rate);

    if (
      form.gst_rate.trim() === '' ||
      !Number.isFinite(gst) ||
      gst < 0 ||
      gst > 100
    ) {
      nextErrors.gst_rate = 'GST must be between 0 and 100.';
    }

    if (form.hsn_code.trim().length > 20) {
      nextErrors.hsn_code = 'Maximum 20 characters.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    await onSubmit({
      product_id: Number(form.product_id),
      batch_number: form.batch_number.trim(),
      purchase_date: form.purchase_date,
      expiry_date: form.expiry_date || null,
      quantity_received: Number(form.quantity_received),
      purchase_price: Number(form.purchase_price),
      selling_price: Number(form.selling_price),
      hsn_code: form.hsn_code.trim() || null,
      gst_rate: Number(form.gst_rate),
    });
  };

  const field = (name, label, options = {}) => (
    <div className="batch-form-field" key={name}>
      <label htmlFor={name}>{label}</label>

      <input
        id={name}
        name={name}
        type={options.type || 'text'}
        value={form[name]}
        onChange={handleChange}
        placeholder={options.placeholder || ''}
        min={options.min}
        max={options.max}
        step={options.step}
        maxLength={options.maxLength}
        required={options.required}
        disabled={loading}
      />

      {errors[name] && (
        <span className="batch-field-error">{errors[name]}</span>
      )}
    </div>
  );

  return (
    <form className="batch-form" onSubmit={handleSubmit}>
      <div className="batch-form-section">
        <h2>Product & Batch Details</h2>
        <p>Select a product and enter its batch information.</p>
      </div>

      <div className="batch-form-grid">
        <div className="batch-form-field">
          <label htmlFor="product_id">Product *</label>

          <select
            id="product_id"
            name="product_id"
            value={form.product_id}
            onChange={handleChange}
            required
            disabled={loading}
          >
            <option value="">Select a product</option>

            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.product_name} ({product.sku})
              </option>
            ))}
          </select>

          {errors.product_id && (
            <span className="batch-field-error">
              {errors.product_id}
            </span>
          )}

          {products.length === 0 && (
            <span className="batch-form-hint">
              No products available. Create or activate a product first.
            </span>
          )}
        </div>

        {field('batch_number', 'Batch Number *', {
          placeholder: 'e.g. BATCH-2026-001',
          maxLength: 100,
          required: true,
        })}

        {field('purchase_date', 'Purchase Date *', {
          type: 'date',
          required: true,
        })}

        {field('expiry_date', 'Expiry Date', {
          type: 'date',
          min: form.purchase_date,
        })}
      </div>

      <div className="batch-form-section">
        <h2>Quantity & Pricing</h2>
        <p>Enter the received quantity and prices for this batch.</p>
      </div>

      <div className="batch-form-grid">
        {field('quantity_received', 'Quantity Received *', {
          type: 'number',
          min: '0.001',
          step: '0.001',
          placeholder: 'e.g. 100',
          required: true,
        })}

        {field('purchase_price', 'Purchase Price *', {
          type: 'number',
          min: 0,
          step: '0.01',
          placeholder: '0.00',
          required: true,
        })}

        {field('selling_price', 'Selling Price *', {
          type: 'number',
          min: 0,
          step: '0.01',
          placeholder: '0.00',
          required: true,
        })}
      </div>

      <div className="batch-form-section">
        <h2>Tax Details</h2>
        <p>Review the HSN code and GST rate for this batch.</p>
      </div>

      <div className="batch-form-grid">
        {field('hsn_code', 'HSN Code', {
          placeholder: 'Enter HSN code',
          maxLength: 20,
        })}

        {field('gst_rate', 'GST Rate (%)', {
          type: 'number',
          min: 0,
          max: 100,
          step: '0.01',
          required: true,
        })}
      </div>

      {selectedProduct && (
        <div className="batch-form-hint">
          Selected product: <strong>{selectedProduct.product_name}</strong>
          {' '}· Unit: {selectedProduct.unit || 'pcs'}
        </div>
      )}

      <div className="batch-form-actions">
        <button
          type="submit"
          className="batch-primary-button"
          disabled={loading || products.length === 0}
        >
          {loading ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default BatchForm;